<template>
  <div>
    <div
      v-if="label.created_by_email !== null"
      class="small text-body-secondary mb-2"
    >
      {{ t("ecolabels.labels.detail.submitted_by") }}
      <a :href="`mailto:${label.created_by_email}`">{{
        label.created_by_email
      }}</a>
    </div>

    <div class="mb-3">
      <div class="d-flex align-items-center gap-2 mb-1">
        <label v-if="isDraft" :for="nameId" class="form-label fw-semibold mb-0">
          {{ t("ecolabels.labels.detail.name") }}
          <span v-if="label.has_entities" class="text-danger">*</span>
        </label>
        <div v-else class="form-label fw-semibold mb-0">
          {{ t("ecolabels.labels.detail.name") }}
        </div>
        <span
          v-if="isDraft"
          class="badge"
          :class="originPillClass(nameDerived)"
        >
          {{ originText(nameDerived) }}
        </span>
      </div>
      <template v-if="isDraft">
        <textarea
          :id="nameId"
          v-model="draft.name"
          class="form-control"
          rows="3"
          maxlength="512"
          :required="label.has_entities"
          @change="scheduleSave"
        />
        <small class="form-text text-muted">
          {{
            t(
              label.has_entities
                ? "ecolabels.labels.detail.name_required_hint"
                : "ecolabels.labels.detail.name_hint"
            )
          }}
        </small>
      </template>
      <div v-else class="label-name">{{ label.name }}</div>
    </div>

    <div class="mb-3">
      <div class="d-flex align-items-center gap-2 mb-1">
        <div class="form-label fw-semibold mb-0">
          {{ t("ecolabels.labels.detail.period") }}
        </div>
        <span
          v-if="isDraft"
          class="badge"
          :class="originPillClass(periodDerived)"
        >
          {{ originText(periodDerived) }}
        </span>
      </div>
      <template v-if="isDraft">
        <div class="mb-2">
          <div class="form-check form-check-inline">
            <input
              :id="fullYearId"
              class="form-check-input"
              type="radio"
              :name="periodModeName"
              :checked="draft.period_mode === 'FULL_YEAR'"
              @change="selectFullYear"
            />
            <label class="form-check-label" :for="fullYearId">
              {{ t("ecolabels.labels.detail.full_year") }}
            </label>
          </div>
          <div class="form-check form-check-inline">
            <input
              :id="dateRangeId"
              class="form-check-input"
              type="radio"
              :name="periodModeName"
              :checked="draft.period_mode === 'DATE_RANGE'"
              @change="selectDateRange"
            />
            <label class="form-check-label" :for="dateRangeId">
              {{ t("ecolabels.labels.detail.date_range") }}
            </label>
          </div>
        </div>
        <div v-if="draft.period_mode === 'FULL_YEAR'">
          <label :for="yearId" class="form-label small">
            {{ t("ecolabels.labels.detail.year") }}
          </label>
          <input
            :id="yearId"
            class="form-control"
            :class="{ 'is-invalid': periodError !== null }"
            type="number"
            inputmode="numeric"
            min="2000"
            max="2100"
            step="1"
            :value="draft.year"
            @input="draft.year = ($event.target as HTMLInputElement).value"
            @change="scheduleSave"
          />
        </div>
        <div v-else class="row g-2">
          <div class="col-sm">
            <label :for="periodStartId" class="form-label small">
              {{ t("ecolabels.labels.detail.period_start") }}
            </label>
            <DatePicker
              :modelValue="toDate(draft.period_start)"
              :inputId="periodStartId"
              :manualInput="false"
              dateFormat="dd/mm/yy"
              showButtonBar
              appendTo="body"
              class="w-100"
              :invalid="periodError !== null"
              @update:modelValue="setPeriodStart"
            />
          </div>
          <div class="col-sm">
            <label :for="periodEndId" class="form-label small">
              {{ t("ecolabels.labels.detail.period_end") }}
            </label>
            <DatePicker
              :modelValue="toDate(draft.period_end)"
              :inputId="periodEndId"
              :manualInput="false"
              dateFormat="dd/mm/yy"
              showButtonBar
              appendTo="body"
              class="w-100"
              :invalid="periodError !== null"
              @update:modelValue="setPeriodEnd"
            />
          </div>
        </div>
        <div v-if="periodError" class="invalid-feedback d-block">
          {{ t(periodError) }}
        </div>
        <div
          v-if="sourceOutsidePeriod"
          class="text-body-secondary small mt-2"
          role="status"
        >
          {{ t("ecolabels.labels.detail.period_outside") }}
        </div>
      </template>
      <div v-else>{{ formatLabelPeriod(label) }}</div>
    </div>

    <div class="mb-3">
      <h6 class="mb-2">{{ t("ecolabels.labels.detail.rows") }}</h6>
      <div class="table-responsive">
        <table class="table table-sm align-middle fs-9 mb-0">
          <thead>
            <tr>
              <th>{{ t("ecolabels.labels.rows.country") }}</th>
              <th class="cell-fit text-end">
                {{ t("ecolabels.labels.rows.volume") }}
              </th>
              <th class="cell-fit">{{ t("ecolabels.labels.rows.type") }}</th>
              <th v-if="label.has_entities">
                {{ t("ecolabels.labels.rows.entity") }}
              </th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="row in label.rows"
              :key="`${row.country}-${row.renewable_type}-${row.entity}`"
            >
              <td>{{ row.country_name }}</td>
              <td class="cell-fit text-end tabular-nums">
                {{ formatNumberWithSpaces(row.volume_mwh) }}
              </td>
              <td class="cell-fit">
                {{
                  t(
                    `ecolabels.renewable_types.${row.renewable_type.toLowerCase()}`
                  )
                }}
              </td>
              <td v-if="label.has_entities">{{ row.entity ?? "" }}</td>
            </tr>
          </tbody>
          <tfoot>
            <tr class="fw-semibold">
              <td>{{ t("ecolabels.labels.detail.total") }}</td>
              <td class="cell-fit text-end tabular-nums">
                {{ formatNumberWithSpaces(label.total_mwh) }}
              </td>
              <td :colspan="label.has_entities ? 2 : 1" />
            </tr>
          </tfoot>
        </table>
      </div>
    </div>

    <div
      v-if="label.problems.length > 0 || sourcesNeedingAttention.length > 0"
      class="text-warning-emphasis small mb-3"
      role="alert"
    >
      <div class="fw-semibold mb-1">
        {{ t("ecolabels.labels.detail.problems") }}
      </div>
      <ul class="mb-0 ps-3">
        <li v-for="(problem, index) in label.problems" :key="index">
          {{ problemText(problem) }}
        </li>
        <li v-for="source in sourcesNeedingAttention" :key="source.id">
          {{
            t("ecolabels.labels.detail.source_needs_attention", {
              file: source.file_name,
            })
          }}
        </li>
      </ul>
    </div>

    <div class="d-flex flex-wrap align-items-center justify-content-end gap-2">
      <template v-if="isDraft">
        <button
          type="button"
          class="btn btn-link text-danger px-0 me-auto"
          :disabled="busy"
          @click="deleteDraft"
        >
          {{ t("ecolabels.labels.actions.delete") }}
        </button>
        <template v-if="isAdmin">
          <button
            type="button"
            class="btn btn-outline-secondary"
            :disabled="busy || !label.can_issue || periodError !== null"
            @click="preview"
          >
            {{ t("ecolabels.labels.actions.preview") }}
          </button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="busy || !label.can_issue || periodError !== null"
            @click="issue"
          >
            {{ t("ecolabels.labels.actions.issue") }}
          </button>
        </template>
      </template>
      <template v-else>
        <button
          v-if="isAdmin && label.status === 'ISSUED'"
          type="button"
          class="btn btn-link text-danger px-0 me-auto"
          :disabled="busy"
          @click="voidIssuedLabel"
        >
          {{ t("ecolabels.labels.actions.void") }}
        </button>
        <span
          v-if="label.reopened_label_id !== null"
          class="text-body-secondary small me-auto"
        >
          {{ t("ecolabels.labels.reopened_as_draft") }}
        </span>
        <button
          v-if="label.can_reopen"
          type="button"
          class="btn btn-outline-primary"
          :disabled="busy"
          @click="reopen"
        >
          {{ t("ecolabels.labels.actions.reopen") }}
        </button>
        <button
          v-if="label.can_download"
          type="button"
          class="btn btn-primary"
          :disabled="busy"
          @click="download"
        >
          <font-awesome-icon icon="download" class="me-1" />
          {{ t("ecolabels.labels.actions.download") }}
        </button>
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { formatNumberWithSpaces } from "@/composables/formatUtils";
import { useGlobalHandlers } from "@/composables/useGlobalHandlers";
import { useServerDraft } from "@/composables/useServerDraft";
import { downloadFromApiResponse } from "@/utils/downloadUtils";
import { isEqual } from "lodash-es";
import { until, useTimeoutFn } from "@vueuse/core";
import DatePicker from "primevue/datepicker";
import { computed, onBeforeUnmount, useId } from "vue";
import { useI18n } from "vue-i18n";
import {
  formatLabelPeriod,
  hasSourceOutsidePeriod,
  isNameDerived,
  isPeriodDerived,
  labelProblemMessage,
  NEUTRAL_PILL_CLASS,
  pickedIsoDate,
  type PickedDate,
} from "./labelDisplay";
import { labelsApi, pdfErrorMessage } from "./labelsApi";
import type {
  Label,
  LabelPeriodMode,
  LabelProblem,
  UpdateLabelDraftRequest,
} from "./labelsTypes";
import { useBusyAction } from "./useBusyAction";

const FULL_YEAR_PATTERN = /^[1-9]\d{3}$/;
const SAVE_DELAY_MS = 300;

interface DraftFields {
  name: string;
  period_mode: LabelPeriodMode;
  year: string;
  period_start: string;
  period_end: string;
}

const props = withDefaults(
  defineProps<{
    label: Label;
    isAdmin: boolean;
    sourcesPending?: boolean;
  }>(),
  { sourcesPending: false }
);

const emit = defineEmits<{
  changed: [];
  deleted: [];
  reopened: [draft: Label];
}>();

const { t } = useI18n();
const { globalSuccessHandler } = useGlobalHandlers();
const nameId = useId();
const periodModeName = useId();
const fullYearId = useId();
const dateRangeId = useId();
const yearId = useId();
const periodStartId = useId();
const periodEndId = useId();

const { busy, run } = useBusyAction();
const draft = useServerDraft(
  () => ({ id: props.label.id, ...savedFields(props.label) }),
  toDraft
);

const isDraft = computed(() => props.label.status === "DRAFT");
const draftRequest = computed(() => toRequest(draft.value));
const isDirty = computed(
  () => !isEqual(draftRequest.value, savedFields(props.label))
);
const periodError = computed(() => draftPeriodError(draft.value));
const sourcesNeedingAttention = computed(() =>
  props.label.sources.filter((source) => source.problems.length > 0)
);
const nameDerived = computed(() =>
  isNameDerived(draft.value.name, props.label.sources)
);
const periodDerived = computed(() =>
  isPeriodDerived(
    draftRequest.value.period_start,
    draftRequest.value.period_end,
    props.label.sources
  )
);
const sourceOutsidePeriod = computed(() =>
  hasSourceOutsidePeriod(
    draftRequest.value.period_start,
    draftRequest.value.period_end,
    props.label.sources
  )
);

const { start: scheduleSave, stop: cancelScheduledSave } = useTimeoutFn(
  () => run(save),
  SAVE_DELAY_MS,
  { immediate: false }
);

onBeforeUnmount(() => void run(save));

function problemText(problem: LabelProblem): string {
  const { key, params } = labelProblemMessage(problem);
  return t(key, params);
}

function originPillClass(derived: boolean): string {
  return derived ? NEUTRAL_PILL_CLASS : "pill-attention";
}

function originText(derived: boolean): string {
  return t(
    derived
      ? "ecolabels.labels.detail.derived"
      : "ecolabels.labels.detail.edited"
  );
}

function selectFullYear() {
  draft.value.period_mode = "FULL_YEAR";
  if (draft.value.year === "")
    draft.value.year = draft.value.period_start.slice(0, 4);
  void scheduleSave();
}

function selectDateRange() {
  draft.value.period_mode = "DATE_RANGE";
  void scheduleSave();
}

function setPeriodStart(date: PickedDate) {
  draft.value.period_start = pickedIsoDate(date);
  void scheduleSave();
}

function setPeriodEnd(date: PickedDate) {
  draft.value.period_end = pickedIsoDate(date);
  void scheduleSave();
}

async function save() {
  if (!isDirty.value || periodError.value !== null) return;
  await labelsApi.updateLabelDraft(props.label.id, draftRequest.value);
  emit("changed");
}

async function preview() {
  cancelScheduledSave();
  await run(async () => {
    await save();
    await sourcesSettled();
    const response = await labelsApi.previewLabelPdf(props.label.id);
    downloadFromApiResponse(response.data, response.headers, pdfFileName());
  }, pdfErrorMessage);
}

async function download() {
  await run(async () => {
    const response = await labelsApi.downloadLabelPdf(props.label.id);
    downloadFromApiResponse(response.data, response.headers, pdfFileName());
  }, pdfErrorMessage);
}

async function issue() {
  if (!window.confirm(t("ecolabels.labels.confirm_issue"))) return;
  cancelScheduledSave();
  await run(async () => {
    await save();
    await sourcesSettled();
    const response = await labelsApi.issueLabel(props.label.id);
    globalSuccessHandler(
      t("ecolabels.labels.issued", { number: response.data.number }),
      ""
    );
    emit("changed");
  });
}

async function deleteDraft() {
  if (!window.confirm(t("ecolabels.labels.confirm_delete"))) return;
  cancelScheduledSave();
  await run(async () => {
    await labelsApi.deleteLabelDraft(props.label.id);
    draft.value = toDraft(savedFields(props.label));
    globalSuccessHandler(t("ecolabels.labels.deleted"), "");
    emit("deleted");
  });
}

async function voidIssuedLabel() {
  if (
    !window.confirm(
      t("ecolabels.labels.confirm_void", { number: props.label.number })
    )
  )
    return;
  await run(async () => {
    const response = await labelsApi.voidLabel(props.label.id);
    globalSuccessHandler(
      t("ecolabels.labels.voided", { number: response.data.number }),
      ""
    );
    emit("changed");
  });
}

async function reopen() {
  const number = props.label.number;
  if (!window.confirm(t("ecolabels.labels.confirm_reopen", { number }))) return;
  await run(async () => {
    const response = await labelsApi.reopenLabel(props.label.id);
    globalSuccessHandler(t("ecolabels.labels.reopened", { number }), "");
    emit("reopened", response.data);
  });
}

function sourcesSettled(): Promise<boolean> {
  return until(() => props.sourcesPending).toBe(false);
}

function pdfFileName(): string {
  return `${props.label.number ?? "label"}.pdf`;
}

function savedFields(label: Label): UpdateLabelDraftRequest {
  return {
    name: label.name,
    period_mode: label.period_mode,
    period_start: label.period_start,
    period_end: label.period_end,
  };
}

function toDraft(fields: UpdateLabelDraftRequest): DraftFields {
  return {
    name: fields.name ?? "",
    period_mode: fields.period_mode,
    year: fields.period_start?.slice(0, 4) ?? "",
    period_start: fields.period_start ?? "",
    period_end: fields.period_end ?? "",
  };
}

function toRequest(fields: DraftFields): UpdateLabelDraftRequest {
  const name = fields.name.trim() === "" ? null : fields.name;
  if (fields.period_mode === "DATE_RANGE")
    return {
      name,
      period_mode: "DATE_RANGE",
      period_start: fields.period_start || null,
      period_end: fields.period_end || null,
    };

  const year = FULL_YEAR_PATTERN.test(fields.year) ? fields.year : null;
  return {
    name,
    period_mode: "FULL_YEAR",
    period_start: year === null ? null : `${year}-01-01`,
    period_end: year === null ? null : `${year}-12-31`,
  };
}

function toDate(isoDate: string): Date | null {
  return isoDate === "" ? null : new Date(`${isoDate}T00:00:00`);
}

function draftPeriodError(fields: DraftFields): string | null {
  if (fields.period_mode === "FULL_YEAR")
    return fields.year === "" || FULL_YEAR_PATTERN.test(fields.year)
      ? null
      : "ecolabels.labels.detail.year_invalid";

  const { period_start: start, period_end: end } = fields;
  if ((start === "") !== (end === ""))
    return "ecolabels.labels.detail.period_incomplete";
  if (start !== "" && start > end)
    return "ecolabels.labels.detail.period_reversed";
  return null;
}
</script>

<style scoped lang="scss">
.label-name {
  white-space: pre-line;
}

.cell-fit {
  width: 1%;
  white-space: nowrap;
}
</style>
